import React, { useState, useEffect } from 'react';
import { Siswa } from '../../types/index.js';
import { api } from '../../services/api.js';
import { Printer, X, FileText, CheckCircle2 } from 'lucide-react';

interface CetakBukuTabunganModalProps {
  siswa: Siswa;
  onClose: () => void;
}

export const CetakBukuTabunganModal: React.FC<CetakBukuTabunganModalProps> = ({ siswa, onClose }) => {
  const [data, setData] = useState<{ halaman_aktif: number; baris_aktif: number; transaksi: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  
  // Custom print state
  const [customStart, setCustomStart] = useState(1);
  const [customEnd, setCustomEnd] = useState(35);
  const [customDateStart, setCustomDateStart] = useState(() => new Date().toISOString().slice(0,10));
  const [customDateEnd, setCustomDateEnd] = useState(() => new Date().toISOString().slice(0,10));
  
  useEffect(() => {
    fetchData();
  }, [siswa.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.getCetakBukuTabungan(siswa.id);
      if (res.success) {
        const trxWithUrut = res.transaksi.map((t: any, index: number) => ({
          ...t,
          no_urut: index + 1
        }));
        setData({ ...res, transaksi: trxWithUrut });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getStyle = () => `
    @page { size: 95mm 140mm; margin: 0; }
    body { margin: 0; padding: 0; }
    .buku { width: 95mm; font-family: 'Courier New', Courier, monospace; font-size: 9pt; }
    .header { text-align:center; border-bottom: 1px solid black; padding: 2mm; margin-bottom: 2mm;}
    table { width: 100%; border-collapse: collapse; table-layout: fixed;}
    th { font-size: 8pt; border-bottom: 1px solid black; padding-bottom: 2px;}
    td { height: 5.2mm; vertical-align: middle; overflow: hidden; white-space: nowrap; font-size: 8pt;}
    .td-center { text-align: center; }
    .td-right { text-align: right; }
    .footer { font-size: 7pt; text-align: right; margin-top: 2mm; }
    .page-break { page-break-after: always; }
  `;

  const generatePrintFullHtml = () => {
    if (!data) return '';
    const { transaksi } = data;
    let html = `<html><head><style>${getStyle()}</style></head><body>`;
    
    const maxBaris = 35;
    const totalPages = Math.ceil(transaksi.length / maxBaris) || 1;
    
    for (let p = 0; p < totalPages; p++) {
      const pageTrx = transaksi.slice(p * maxBaris, (p + 1) * maxBaris);
      
      html += `<div class="buku">
        <div class="header">SDMT Ponorogo - Buku Tabungan<br>NIS: ${siswa.nis} - ${siswa.nama.substring(0, 15)} - ${siswa.kelasNama}</div>
        <table>
          <tr>
            <th style="width: 5%">No</th>
            <th style="width: 10%">Kode</th>
            <th style="width: 15%">Tanggal</th>
            <th style="width: 20%">Debet</th>
            <th style="width: 20%">Kredit</th>
            <th style="width: 20%">Saldo</th>
            <th style="width: 10%">Ket</th>
          </tr>`;
          
      // Render transactions
      for (let i = 0; i < maxBaris; i++) {
        const t = pageTrx[i];
        if (t) {
          const dt = new Date(t.tanggal);
          const tglStr = `${dt.getDate().toString().padStart(2, '0')}-${(dt.getMonth()+1).toString().padStart(2,'0')}-${dt.getFullYear().toString().slice(2)}`;
          const kode = t.jenis === 'SETOR' ? 'ST' : 'TR';
          const debet = t.jenis === 'TARIK' || t.jenis === 'BAYAR_TAGIHAN' ? t.nominal.toLocaleString('id-ID') : '0';
          const kredit = t.jenis === 'SETOR' ? t.nominal.toLocaleString('id-ID') : '0';
          const saldo = t.saldoSesudah.toLocaleString('id-ID');
          const ket = t.keterangan ? t.keterangan.substring(0, 5) : ''; // Potong keterangan agar muat
          
          html += `<tr>
            <td class="td-center">${t.no_urut}</td>
            <td class="td-center">${kode}</td>
            <td class="td-center">${tglStr}</td>
            <td class="td-right">${debet}</td>
            <td class="td-right">${kredit}</td>
            <td class="td-right">${saldo}</td>
            <td class="td-center">${ket}</td>
          </tr>`;
        } else {
          // Empty row
          html += `<tr><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>`;
        }
      }
      
      html += `</table>
        <div class="footer">Hal ${p+1}/${totalPages} - Dicetak ${new Date().toLocaleDateString('id-ID')} - Petugas: Admin</div>
      </div>`;
      
      if (p < totalPages - 1) {
        html += `<div class="page-break"></div>`;
      }
    }
    
    html += `</body></html>`;
    return html;
  };

  const generatePrintUnprintedHtml = (unprintedTrx: any[], startingRowPosition: number) => {
    let html = `<html><head><style>${getStyle()}</style></head><body>`;
    // Header space (approx 12mm) + (rowPosition - 1) * 5.2mm
    const marginTopMm = 12 + ((startingRowPosition - 1) * 5.2);
    
    html += `<div class="buku">
      <div style="height: ${marginTopMm}mm;"></div>
      <table>`;
    
    unprintedTrx.forEach((trx) => {
      const dt = new Date(trx.tanggal);
      const tglStr = `${dt.getDate().toString().padStart(2, '0')}-${(dt.getMonth()+1).toString().padStart(2,'0')}-${dt.getFullYear().toString().slice(2)}`;
      const kode = trx.jenis === 'SETOR' ? 'ST' : 'TR';
      const debet = trx.jenis === 'TARIK' || trx.jenis === 'BAYAR_TAGIHAN' ? trx.nominal.toLocaleString('id-ID') : '0';
      const kredit = trx.jenis === 'SETOR' ? trx.nominal.toLocaleString('id-ID') : '0';
      const saldo = trx.saldoSesudah.toLocaleString('id-ID');
      const ket = trx.keterangan ? trx.keterangan.substring(0, 5) : '';
      
      html += `<tr>
        <td class="td-center" style="width: 5%">${trx.no_urut}</td>
        <td class="td-center" style="width: 10%">${kode}</td>
        <td class="td-center" style="width: 15%">${tglStr}</td>
        <td class="td-right" style="width: 20%">${debet}</td>
        <td class="td-right" style="width: 20%">${kredit}</td>
        <td class="td-right" style="width: 20%">${saldo}</td>
        <td class="td-center" style="width: 10%">${ket}</td>
      </tr>`;
    });
      
    html += `</table></div></body></html>`;
    return html;
  };

  const doPrint = (htmlContent: string) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();
      
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 500);
    }
  };

  const handlePrintFull = () => {
    if (!data) return;
    setPrinting(true);
    const html = generatePrintFullHtml();
    doPrint(html);
    
    // Log
    if (data.transaksi.length > 0) {
      const lastTrx = data.transaksi[data.transaksi.length - 1];
      api.logCetakBuku(siswa.id, {
        id_transaksi: lastTrx.id,
        halaman_ke: lastTrx.halaman_ke,
        baris_ke: lastTrx.baris_ke,
        tipe_cetak: 'FULL_PAGE',
        petugas: 'Admin'
      });
    }
    
    setTimeout(() => { setPrinting(false); onClose(); }, 1500);
  };

  const handlePrintBaris = () => {
    if (!data || data.transaksi.length === 0) return;
    
    // Find unprinted transactions
    const totalPrinted = ((data.halaman_aktif - 1) * 35) + data.baris_aktif;
    const unprintedTrx = data.transaksi.slice(totalPrinted);
    
    if (unprintedTrx.length === 0) {
      alert("Semua transaksi sudah tercetak.");
      return;
    }
    
    // We only print up to the end of the current page (max 35).
    const spaceLeftOnPage = 35 - data.baris_aktif;
    const trxToPrintNow = unprintedTrx.slice(0, spaceLeftOnPage);
    
    setPrinting(true);
    
    const startingBaris = data.baris_aktif + 1;
    const html = generatePrintUnprintedHtml(trxToPrintNow, startingBaris);
    doPrint(html);
    
    const lastPrintedTrx = trxToPrintNow[trxToPrintNow.length - 1];
    let newBarisAktif = data.baris_aktif + trxToPrintNow.length;
    let newHalamanAktif = data.halaman_aktif;
    
    if (newBarisAktif >= 35) {
      newHalamanAktif += 1;
      newBarisAktif = 0;
    }
    
    api.logCetakBuku(siswa.id, {
      id_transaksi: lastPrintedTrx.id,
      halaman_ke: newHalamanAktif,
      baris_ke: newBarisAktif,
      tipe_cetak: 'PER_BARIS',
      petugas: 'Admin'
    });
    
    setTimeout(() => { 
      setPrinting(false); 
      fetchData(); // Reload so we can see updated status
    }, 1500);
  };

  const handlePrintCustom = () => {
    if (!data || data.transaksi.length === 0) return;
    if (customStart > customEnd) {
      alert("Baris awal tidak boleh lebih besar dari baris akhir.");
      return;
    }
    
    // Filter by no_urut (which maps to row number overall)
    const customTrx = data.transaksi.filter(t => t.no_urut >= customStart && t.no_urut <= customEnd);
    if (customTrx.length === 0) {
      alert("Tidak ada data pada rentang baris tersebut.");
      return;
    }
    
    setPrinting(true);
    
    // Cetak kustom mencetak persis pada posisinya (mirip mode 1, tapi untuk baris kustom)
    // Tujuannya menambal baris yang terlewat pada buku fisik.
    // Jika rentang melebihi 1 halaman, batasi per halaman dulu
    let html = `<html><head><style>${getStyle()}</style></head><body>`;
    
    let currentHalaman = Math.ceil(customTrx[0].no_urut / 35);
    let barisAwalDiHalaman = ((customTrx[0].no_urut - 1) % 35) + 1;
    
    const marginTopMm = 12 + ((barisAwalDiHalaman - 1) * 5.2);
    
    html += `<div class="buku">
      <div style="height: ${marginTopMm}mm;"></div>
      <table>`;
      
    customTrx.forEach((trx) => {
      // Peringatan: cetak kustom ini sederhana, tidak memotong ke page 2 jika melewatinya.
      // Diharapkan user mencetak per bagian halaman.
      if (Math.ceil(trx.no_urut / 35) !== currentHalaman) return;
      
      const dt = new Date(trx.tanggal);
      const tglStr = `${dt.getDate().toString().padStart(2, '0')}-${(dt.getMonth()+1).toString().padStart(2,'0')}-${dt.getFullYear().toString().slice(2)}`;
      const kode = trx.jenis === 'SETOR' ? 'ST' : 'TR';
      const debet = trx.jenis === 'TARIK' || trx.jenis === 'BAYAR_TAGIHAN' ? trx.nominal.toLocaleString('id-ID') : '0';
      const kredit = trx.jenis === 'SETOR' ? trx.nominal.toLocaleString('id-ID') : '0';
      const saldo = trx.saldoSesudah.toLocaleString('id-ID');
      const ket = trx.keterangan ? trx.keterangan.substring(0, 5) : '';
      
      html += `<tr>
        <td class="td-center" style="width: 5%">${trx.no_urut}</td>
        <td class="td-center" style="width: 10%">${kode}</td>
        <td class="td-center" style="width: 15%">${tglStr}</td>
        <td class="td-right" style="width: 20%">${debet}</td>
        <td class="td-right" style="width: 20%">${kredit}</td>
        <td class="td-right" style="width: 20%">${saldo}</td>
        <td class="td-center" style="width: 10%">${ket}</td>
      </tr>`;
    });
    
    html += `</table></div></body></html>`;
    doPrint(html);
    
    setTimeout(() => { setPrinting(false); }, 1500);
  };

  const handlePrintDateCustom = () => {
    if (!data || data.transaksi.length === 0) return;
    
    const startDt = new Date(customDateStart);
    const endDt = new Date(customDateEnd);
    endDt.setHours(23, 59, 59, 999);
    
    if (startDt > endDt) {
      alert("Tanggal awal tidak boleh lebih besar dari tanggal akhir.");
      return;
    }
    
    // Filter berdasarkan rentang tanggal
    const customTrx = data.transaksi.filter((t) => {
      const dt = new Date(t.tanggal);
      return dt >= startDt && dt <= endDt;
    });
    
    if (customTrx.length === 0) {
      alert("Tidak ada data pada rentang tanggal tersebut.");
      return;
    }
    
    setPrinting(true);
    
    // Untuk Mode Tanggal, kita akan asumsikan mencetak lembaran ulang murni,
    // mirip cetak full tapi HANYA tanggal yang difilter. (Mulai dari baris paling atas).
    let html = `<html><head><style>${getStyle()}</style></head><body>`;
    
    // Pisahkan per 35 transaksi
    const CHUNK_SIZE = 35;
    for (let i = 0; i < customTrx.length; i += CHUNK_SIZE) {
      const pageTrx = customTrx.slice(i, i + CHUNK_SIZE);
      html += `<div class="buku"><table>`;
      for (let j = 0; j < 35; j++) {
        const t = pageTrx[j];
        if (t) {
          const dt = new Date(t.tanggal);
          const tglStr = `${dt.getDate().toString().padStart(2, '0')}-${(dt.getMonth()+1).toString().padStart(2,'0')}-${dt.getFullYear().toString().slice(2)}`;
          const kode = t.jenis === 'SETOR' ? 'ST' : 'TR';
          const debet = t.jenis === 'TARIK' || t.jenis === 'BAYAR_TAGIHAN' ? t.nominal.toLocaleString('id-ID') : '0';
          const kredit = t.jenis === 'SETOR' ? t.nominal.toLocaleString('id-ID') : '0';
          const saldo = t.saldoSesudah.toLocaleString('id-ID');
          const ket = t.keterangan ? t.keterangan.substring(0, 5) : '';
          
          html += `<tr>
            <td class="td-center" style="width: 5%">${t.no_urut}</td>
            <td class="td-center" style="width: 10%">${kode}</td>
            <td class="td-center" style="width: 15%">${tglStr}</td>
            <td class="td-right" style="width: 20%">${debet}</td>
            <td class="td-right" style="width: 20%">${kredit}</td>
            <td class="td-right" style="width: 20%">${saldo}</td>
            <td class="td-center" style="width: 10%">${ket}</td>
          </tr>`;
        } else {
          html += `<tr><td colspan="7">&nbsp;</td></tr>`;
        }
      }
      html += `</table></div>`;
    }
    
    html += `</body></html>`;
    doPrint(html);
    
    setTimeout(() => { setPrinting(false); }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-0 border border-slate-200 animate-in fade-in overflow-hidden flex flex-col">
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Printer className="w-4 h-4 text-sky-600" />
              Cetak Buku Tabungan
            </h3>
            <p className="text-xs text-slate-500 mt-1">NIS: {siswa.nis} - {siswa.nama}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-500">Memuat data...</div>
          ) : (
            <div className="space-y-6">
              <div className="bg-sky-50 border border-sky-100 p-4 rounded-lg flex items-start gap-3">
                <FileText className="w-5 h-5 text-sky-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-sky-900 mb-1">Status Buku Tabungan</h4>
                  <ul className="text-xs text-sky-800 space-y-1">
                    <li>Halaman Aktif: <span className="font-bold">{data?.halaman_aktif}</span></li>
                    <li>Baris Terakhir Dicetak: <span className="font-bold">{data?.baris_aktif}</span> / 35</li>
                    <li>Total Transaksi: <span className="font-bold">{data?.transaksi.length}</span> baris</li>
                    <li>Belum Tercetak: <span className="font-bold text-amber-700">{Math.max(0, (data?.transaksi.length || 0) - (((data?.halaman_aktif || 1) - 1) * 35 + (data?.baris_aktif || 0)))}</span> baris</li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handlePrintBaris}
                  disabled={printing || data?.transaksi.length === 0}
                  className="flex items-center justify-between px-4 py-3 border-2 border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl transition disabled:opacity-50"
                >
                  <div className="text-left">
                    <span className="font-bold block text-sm">Mode 1: Cetak Transaksi Baru</span>
                    <span className="text-[11px] opacity-80">Otomatis mencetak baris yang belum diprint.</span>
                  </div>
                  <Printer className="w-5 h-5" />
                </button>

                <button
                  onClick={handlePrintFull}
                  disabled={printing || data?.transaksi.length === 0}
                  className="flex items-center justify-between px-4 py-3 border border-slate-300 hover:border-sky-500 hover:bg-sky-50 text-slate-700 rounded-xl transition disabled:opacity-50"
                >
                  <div className="text-left">
                    <span className="font-bold block text-sm">Mode 2: Cetak Ulang Penuh</span>
                    <span className="text-[11px] opacity-80">Cetak semua baris transaksi (max 35 baris/halaman).</span>
                  </div>
                  <FileText className="w-5 h-5" />
                </button>

                <div className="border border-slate-300 rounded-xl overflow-hidden mt-2">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-300">
                    <span className="font-bold block text-sm text-slate-700">Mode 3: Cetak Ulang Spesifik (Tambal Buku)</span>
                    <span className="text-[11px] text-slate-500">Cetak ulang pada posisi baris tertentu tanpa menimpa baris lain.</span>
                  </div>
                  <div className="p-3 bg-white flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="flex-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Dari Baris No</label>
                        <input 
                          type="number" 
                          value={customStart}
                          onChange={(e) => setCustomStart(Number(e.target.value))}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-sm text-center focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                      <span className="text-slate-400 mt-4">-</span>
                      <div className="flex-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Sampai Baris</label>
                        <input 
                          type="number" 
                          value={customEnd}
                          onChange={(e) => setCustomEnd(Number(e.target.value))}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-sm text-center focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handlePrintCustom}
                      disabled={printing || data?.transaksi.length === 0}
                      className="mt-4 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded font-semibold text-xs flex items-center gap-1 transition shadow disabled:opacity-50"
                    >
                      <Printer className="w-4 h-4" />
                      Cetak
                    </button>
                  </div>
                </div>

                <div className="border border-slate-300 rounded-xl overflow-hidden mt-2">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-300">
                    <span className="font-bold block text-sm text-slate-700">Mode 4: Cetak Ulang Berdasarkan Tanggal</span>
                    <span className="text-[11px] text-slate-500">Cetak lembaran utuh khusus transaksi pada rentang tanggal terpilih.</span>
                  </div>
                  <div className="p-3 bg-white flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="flex-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Mulai Tgl</label>
                        <input 
                          type="date" 
                          value={customDateStart}
                          onChange={(e) => setCustomDateStart(e.target.value)}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                      <span className="text-slate-400 mt-4">-</span>
                      <div className="flex-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Sampai Tgl</label>
                        <input 
                          type="date" 
                          value={customDateEnd}
                          onChange={(e) => setCustomDateEnd(e.target.value)}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handlePrintDateCustom}
                      disabled={printing || data?.transaksi.length === 0}
                      className="mt-4 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded font-semibold text-xs flex items-center gap-1 transition shadow disabled:opacity-50"
                    >
                      <Printer className="w-4 h-4" />
                      Cetak
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
